import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, of, timer, takeUntil, concatMap, catchError, filter, take, first } from 'rxjs';
import { MessageService } from 'primeng/api';
import { SkillService } from '../../services/skill.service';
import { SkillPersistenceService } from '../../services/skill-persistence.service';
import { SkillDetail } from '../../models/skill-detail.model';
import { SkillConstants } from '../../constants/skill-constants';
import { PollCompletionService, PollResult } from '../../../core/services/poll-completion.service';
import { AssignmentGroupInteractionService } from '../../../system-admin/assignment-group/services/assignment-group-interaction.service';
import { AuditTrialDetails } from '../../../shared/models/audits/audit-trial-details';
import { NameCode } from '../../../shared/models/name-code';
import { SkillTrainingItemsListConfig } from '../../models/skill-training-items-list-config.model';
import { TrainingItemGroupService } from '../../../training-item/services/training-item-group.service';

@Component({
  selector: 'app-skill-details',
  templateUrl: './skill-details.component.html',
  styleUrls: ['./skill-details.component.scss']
})
export class SkillDetailsComponent implements OnInit, OnDestroy {
  code: string = '';
  skill: SkillDetail | null = null;
  isLoading: boolean = true;
  errorMessage: string | null = null;
  activeIndex: number = 0;
  accessToSkillDetails: boolean = false;
  isTrainingItemSliderOpen: boolean = false;
  isSkillSliderOpen: boolean = false;
  isUnassignTrainingItemGroupSliderOpen: boolean = false;
  selectedTrainingItemGroups: string[] = [];
  trainingItemGroups: NameCode[] = [];
  isTrainingItemsTabActive: boolean = false; // Lazy load flag for training items tab
  trainingItemsRefreshVersion: number = 0;
  trainingItemsListConfig: SkillTrainingItemsListConfig = {
    skillCode: '',
    isTrainingItemStatusUpdateEnabled: false,
    isUnassignTrainingItemGroupVisible: false,
    refreshVersion: 0
  };
  isAuditTabActive: boolean = false; // Lazy load flag for audit tab
  auditTrailData: AuditTrialDetails[] = [];
  isAuditDataLoading: boolean = false;
  auditLoadErrorMessage: string | null = null;
  private destroy$ = new Subject<void>();
  private static readonly UnassignRefreshIntervalMs = 1500;
  private static readonly UnassignRefreshMaxAttempts = 20;
  private unassignedTrainingItemGroups: string[] = [];
  private isAwaitingUnassignPersistence = false;

  constructor(
    private route: ActivatedRoute,
    private skillService: SkillService,
    private skillPersistenceService: SkillPersistenceService,
    private messageService: MessageService,
    private pollCompletionService: PollCompletionService,
    private assignmentGroupInteractionService: AssignmentGroupInteractionService,
    private trainingItemGroupService: TrainingItemGroupService
  ) {
    // Subscribe to poll completion events (following document-details pattern)
    this.pollCompletionService.completion$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: PollResult) => {
          this.handlePollCompletion(result);
        }
      });

    this.skillPersistenceService.closeAndResetTrainingItemSlider$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClose) => {
        if (isClose) {
          this.isTrainingItemSliderOpen = false;
        }
      });

    this.skillPersistenceService.closeAndResetSkillSlider$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isClose) => {
        if (isClose) {
          this.isSkillSliderOpen = false;
          this.loadSkillDetails();
        }
      });

  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.code = params['skillId'];
        if (this.code) {
          this.loadSkillDetails();
        } else {
          this.errorMessage = 'Skill ID not found in route';
          this.accessToSkillDetails = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Load skill details from the service
   */
  loadSkillDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;
    
    this.skillService.getSkillByCode(this.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: SkillDetail) => {
          this.skill = data;
          this.updateTrainingItemsListConfig();
          this.accessToSkillDetails = true;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading skill details:', error);
          this.skill = null;
          if (error.status === 403) {
            this.errorMessage = error.error?.detail || SkillConstants.SKILL_ACCESS_DENIED_ERROR;
          } else {
            this.errorMessage = '';
          }
          this.accessToSkillDetails = false;
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle tab change event for lazy loading training items
   */
  onTabChange(event: any): void {
    this.activeIndex = event.index;

    // Lazy load training items tab (index 1)
    if (event.index === 1) {
      this.isTrainingItemsTabActive = true;
    }

    // Lazy load audit tab (index 2)
    if (event.index === 2) {
      this.isAuditTabActive = true;
      this.loadAuditTrail();
    }
  }

  /**
   * Fetch the audit trail for the current skill (lazy-loaded when the
   * Audit tab is opened the first time, then refreshed on subsequent opens
   * so newly published events are visible without reloading the page).
   */
  loadAuditTrail(): void {
    if (!this.skill) {
      return;
    }
    this.isAuditDataLoading = true;
    this.auditLoadErrorMessage = null;
    this.skillService.getSkillAudit(this.skill.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: AuditTrialDetails[]) => {
          this.auditTrailData = data || [];
          this.isAuditDataLoading = false;
        },
        error: (error) => {
          console.error('Error loading skill audit trail:', error);
          this.auditTrailData = [];
          this.auditLoadErrorMessage = 'Failed to load audit trail.';
          this.isAuditDataLoading = false;
        }
      });
  }

  /**
   * Handles poll completion events from the polling service
   */
  private handlePollCompletion(result: PollResult): void {
    switch (result.domain) {
      case SkillConstants.PollDomain_LinkTrainingItems:
        if (result.status !== 'completed') {
          this.pollCompletionService.displayPollError(result, 'linking training items');
          return;
        }
        this.handleLinkTrainingItemsCompletion();
        break;
      case SkillConstants.PollDomain_UnassignTrainingItemGroup:
        if (result.status !== 'completed') {
          this.pollCompletionService.displayPollError(result, 'unassigning training item groups');
          return;
        }
        this.handleUnassignTrainingItemGroupCompletion();
        break;
    }
  }

  private handleLinkTrainingItemsCompletion(): void {
    this.messageService.add({
      severity: SkillConstants.Severity_Success,
      summary: SkillConstants.LinkedTrainingItems_Successfully_Updated_Message,
      life: SkillConstants.Toast_Message_Life
    });
    this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
    this.loadSkillDetails();
  }

  /** Refreshes skill details and linked training items after TIG unassignment is persisted. */
  private handleUnassignTrainingItemGroupCompletion(): void {
    this.messageService.add({
      severity: SkillConstants.Severity_Success,
      summary: SkillConstants.TrainingItemGroup_Unassigned_Success_Message,
      sticky: false
    });
    this.refreshAfterUnassignPersisted();
  }

  /**
   * Re-reads the skill until the unassigned groups are gone from its active training items,
   * so the list is only refreshed once the backend projection actually reflects the change.
   */
  private refreshAfterUnassignPersisted(): void {
    const skillCode = this.skill?.code;
    if (!skillCode || this.isAwaitingUnassignPersistence) {
      return;
    }

    const unassignedGroups = [...this.unassignedTrainingItemGroups];
    this.isAwaitingUnassignPersistence = true;

    timer(0, SkillDetailsComponent.UnassignRefreshIntervalMs)
      .pipe(
        take(SkillDetailsComponent.UnassignRefreshMaxAttempts),
        concatMap(() => this.skillService.getSkillByCode(skillCode).pipe(catchError(() => of(null)))),
        filter((skill): skill is SkillDetail => skill !== null),
        first(skill => !this.hasActiveTrainingItemGroups(skill, unassignedGroups), null),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (skill) => {
          this.isAwaitingUnassignPersistence = false;
          this.unassignedTrainingItemGroups = [];
          if (skill) {
            this.skill = skill;
          }
          this.trainingItemsRefreshVersion++;
          this.updateTrainingItemsListConfig();
          if (!skill) {
            this.loadSkillDetails();
          }
        },
        error: () => {
          this.isAwaitingUnassignPersistence = false;
          this.unassignedTrainingItemGroups = [];
          this.loadSkillDetails();
          this.trainingItemsRefreshVersion++;
          this.updateTrainingItemsListConfig();
        }
      });
  }

  /** True while any active training item still references one of the supplied group codes. */
  private hasActiveTrainingItemGroups(skill: SkillDetail, groupCodes: string[]): boolean {
    if (groupCodes.length === 0) {
      return false;
    }

    return (skill.linkedTrainingItems ?? []).some(item =>
      item.status === SkillConstants.Skill_StatusActive &&
      (item.trainingItemGroups ?? []).some(groupCode =>
        groupCodes.some(unassigned => unassigned.toLowerCase() === groupCode?.toLowerCase())));
  }

  /** Rebuilds the child list config so the linked training items panel picks up the latest state. */
  private updateTrainingItemsListConfig(): void {
    this.trainingItemsListConfig = {
      skillCode: this.skill?.code || '',
      isTrainingItemStatusUpdateEnabled: !!this.skill?.skillAuthorizationFlag?.isTrainingItemStatusUpdateEnabled,
      isUnassignTrainingItemGroupVisible: !!this.skill?.isAddTrainingItemGroupButtonVisible,
      refreshVersion: this.trainingItemsRefreshVersion
    };
  }

  /** Formats the skill validity period for display in the General tab. */
  getValidityPeriodDisplay(): string {
    const range = this.skill?.validityPeriod?.range;
    const interval = this.skill?.validityPeriod?.interval;

    if (range == null || interval == null) {
      return '';
    }

    const units = ['Day', 'Month', 'Year'];
    const unit = units[interval];
    if (!unit) {
      return range.toString();
    }

    return `${range} ${unit}${range === 1 ? '' : 's'}`;
  }

  /**
   * Get the appropriate error message to display
   */
  getSkillDetailsErrorMessage(): string {
    return this.errorMessage || SkillConstants.SKILL_LOAD_ERROR;
  }

  openAddTrainingItems(){
    this.isTrainingItemSliderOpen = !this.isTrainingItemSliderOpen;
  }

  editSkill(): void {
    if (!this.isEditSkillEnabled()) {
      return;
    }

    this.isSkillSliderOpen = true;
  }

  closeSkillSlider(isSliderActive: boolean): void {
    this.isSkillSliderOpen = isSliderActive;
    if (!isSliderActive) {
      this.loadSkillDetails();
    }
  }

  isEditSkillEnabled(): boolean {
    return !!this.skill?.skillAuthorizationFlag?.isEditSkillEnabled;
  }

  closeTrainingItemSlider(isSliderActive : boolean){
    this.isTrainingItemSliderOpen = isSliderActive;
  }

  /**
   * Open the unassign training item group slider
   */
  openUnassignTrainingItemGroupSlider(): void {
    if (!this.skill) {
      return;
    }

    this.skillService.getSkillByCode(this.skill.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (currentSkill) => {
          this.skill = currentSkill;
          this.loadAvailableTrainingItemGroups();
        },
        error: () => {
          this.loadAvailableTrainingItemGroups();
        }
      });
  }

  /** Resolves names for the active TIG references in the current skill snapshot. */
  private loadAvailableTrainingItemGroups(): void {
    const availableGroups = this.getAvailableTrainingItemGroups();
    const groupCodes = availableGroups.map(group => group.code);

    if (groupCodes.length === 0) {
      this.trainingItemGroups = [];
      this.isUnassignTrainingItemGroupSliderOpen = true;
      return;
    }

    this.trainingItemGroupService.getTrainingItems(groupCodes)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (trainingItemGroups) => {
          this.trainingItemGroups = trainingItemGroups.map((group: { number: string; name: string }) => ({
            code: group.number,
            name: group.name || group.number
          }));
          this.isUnassignTrainingItemGroupSliderOpen = true;
        },
        error: () => {
          this.trainingItemGroups = availableGroups;
          this.isUnassignTrainingItemGroupSliderOpen = true;
        }
      });
  }

  closeUnassignTrainingItemGroupSlider(isSliderActive: boolean): void {
    this.isUnassignTrainingItemGroupSliderOpen = isSliderActive;
    if (!isSliderActive) {
      this.selectedTrainingItemGroups = [];
    }
  }

  selectedTrainingItemGroupsList(selectedGroups: string[]): void {
    this.selectedTrainingItemGroups = selectedGroups;
  }

  unassignTrainingItemGroupsFromSkill(): void {
    if (!this.skill || this.selectedTrainingItemGroups.length === 0) {
      return;
    }

    const unassignedGroups = [...this.selectedTrainingItemGroups];
    
    this.skillService.unassignTrainingItemGroups(this.skill.code, {
      trainingItemGroupCodes: unassignedGroups
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.unassignedTrainingItemGroups = unassignedGroups;
          // Persistence is deferred, so re-read the skill until the change is visible
          // instead of refreshing on the API acknowledgement alone.
          if (response?.affectedItems) {
            this.refreshAfterUnassignPersisted();
          } else {
            this.handleUnassignTrainingItemGroupCompletion();
          }
          this.closeUnassignTrainingItemGroupSlider(false);
        },
        error: (error) => {
          this.messageService.add({
            severity: SkillConstants.Severity_Err,
            summary: error?.error?.detail || 'Failed to unassign training item groups. Please try again.',
            sticky: false
          });
          this.closeUnassignTrainingItemGroupSlider(false);
        }
      });
  }

  private getAvailableTrainingItemGroups(): NameCode[] {
    const groupCodes = new Set<string>();

    for (const item of this.skill?.linkedTrainingItems ?? []) {
      if (item.status !== SkillConstants.Skill_StatusActive) {
        continue;
      }

      for (const groupCode of item.trainingItemGroups ?? []) {
        groupCodes.add(groupCode);
      }
    }

    return Array.from(groupCodes)
      .sort((first, second) => first.localeCompare(second))
      .map((code) => ({ code, name: code }));
  }
}
